import * as smblsModule from 'smbls'
import {
  deepMerge,
  replaceOctalEscapeSequences,
  deepClone,
  loadRemoteCSS,
  loadRemoteScript,
  loadJavascript,
  isLocal,
  deepDestringifyFunctions
} from 'smbls'

import { loadModule } from './depdencencies'

const dotsBase = {
  backgroundPosition: 'top',
  backgroundRepeat: 'repeat',
  backgroundSize: '20px 20px'
}

const platformUI = {
  gradient: {
    dotsDark: 'radial-gradient(#435067 6%, transparent 6%)',
    dotsLight: 'radial-gradient(#d9d9db 6%, transparent 6%)'
  },
  reset: {
    head: { overscrollBehaviorX: 'none' },
    body: { overscrollBehaviorX: 'none' }
  },
  theme: {
    dots: {
      ...dotsBase,
      '@dark': { backgroundImage: 'dotsDark', ...dotsBase },
      '@light': { backgroundImage: 'dotsLight', ...dotsBase }
    }
  },
  animation: {
    fadeIn: {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 }
    },
    blink: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0 },
      '100%': { opacity: 1 }
    },
    componentBlink: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.35 },
      '100%': { opacity: 1 }
    }
  }
}

export function prepareDesignSystem(data, scope) {
  const globalTheme = scope?.sceneTheme || data?.designSystem?.globalTheme
  const designSystem = data.designSystem || {}

  if (designSystem.reset?.body) {
    designSystem.reset.body.position = 'absolute'
    designSystem.reset.body.inset = '0 0 0 0'
  }

  return deepMerge(designSystem, { globalTheme, ...platformUI })
}

export function prepareComponent(scope, state, code) {
  const el = this
  const props = state.props || {}

  const demoCode = el.prepareCode(code)

  const extend = [demoCode]

  if (state.state) {
    extend.push({ state: state.state })
  }
  extend.push({
    margin: 'auto',
    fontSize: props.base && `${props.base}px`,
    '--spacing-ratio': state.ratio,
    variant: state.variant,
    transition: 'all, B, defaultBezier',
    ':focus-within': {
      outline: 'none'
    }
  })

  const appjs = el.getFiles('app.js')
  if (scope.useAppjs && appjs?.code) {
    extend.push(appjs?.code)
  }

  return deepClone({ extend })
}

export function prepareDemoApp(scope, state, code) {
  const el = this
  if (scope.renderPureComponent) return code

  const demoComponent = el.prepareComponent(scope, state, code)

  return deepClone({
    extends: ['Flex'],
    props: el.prepareFrameProps(scope, state),
    data: {},
    demoComponent,
    on: scope.appEvents
  })
}

export async function prepareDependencies(scope, data, deps) {
  const meta = scope.document.createElement('meta')
  meta.name = 'viewport'
  meta.content = 'width=device-width, user-scalable=no'
  scope.document.head.appendChild(meta)

  if (scope.canvas) {
    await loadRemoteScript(
      'https://pkg.symbo.ls/panzoom@9.4.3/dist/panzoom.min.js',
      { document: scope.document }
    )
    await loadRemoteScript(
      'https://pkg.symbo.ls/gridstack@10.3.1/dist/gridstack-all.js',
      { document: scope.document }
    )
    await loadRemoteCSS(
      'https://pkg.symbo.ls/gridstack@10.3.1/dist/gridstack.css',
      { document: scope.document }
    )
  }

  const isSmblsLocal =
    true || isLocal() || this.getUserSettings('smblsFromLocal')

  // Ensure iframe window.packages exists for loadModule
  if (!scope.window.packages) scope.window.packages = {}

  // Load smbls inside the iframe.
  // Local dev: browser bundle from local server, prod: esm.sh CDN
  if (isSmblsLocal) {
    const localUrl =
      'http://localhost:5500/packages/smbls/dist/browser/index.js'
    try {
      // Shim Node globals for browser bundle
      if (!scope.window.process) scope.window.process = { env: {} }
      const mod = await loadModule('smbls', localUrl, scope, null, '')
      scope.window.smbls = mod
    } catch (e) {
      this.warn('Problem with local smbls', e)
      scope.window.smbls = smblsModule
    }
  } else {
    const remoteUrl = 'https://esm.sh/smbls'
    const lastUpdated = this.state.root.updatedAt
    const randomize = lastUpdated || Math.random()
    const cachingSuffix = `?${randomize}`
    try {
      const mod = await loadModule(
        'smbls',
        remoteUrl + cachingSuffix,
        scope,
        null,
        ''
      )
      scope.window.smbls = mod
    } catch (e) {
      this.warn('Problem with smbls', e)
      scope.window.smbls = smblsModule
    }
  }
}

export async function prepareContext(scope, data) {
  const el = this
  const sceneMode = this.call('exec', scope.sceneMode)

  const __cache = scope.__cache
  if (__cache) Object.assign(data, __cache) // todo: need to check

  console.log('Preparing context with sceneMode:', sceneMode)

  if (sceneMode === 'basic') {
    return {
      components: {},
      pages: { '/': data },
      designSystem: {},
      router: { pushState: false }
    }
  }

  if (sceneMode === 'app') {
    return { ...data, router: { pushState: false } }
  }

  return {
    ...data,
    designSystem: el.prepareDesignSystem(data, scope),
    utils: el.prepareUtils(data, scope),
    methods: el.prepareMethods(data, scope),
    sharedLibraries: (data?.sharedLibraries || []).map(
      (v) => deepDestringifyFunctions(v) || {}
    ),
    type: ['marketplace', 'platform'].includes(scope?.projectType)
      ? 'template'
      : 'app',
    initOptions: el.prepareInitOptions(scope),
    domqlOptions: { allowRefReference: true },
    editor: {
      verbose: false,
      inspect: false,
      liveSync: false,
      remote: false
    },
    ...(scope.useRouter && { router: scope.routerOptions })
  }
}

export async function renderStateComponent(code) {
  if (!code) return
  const el = this
  const s = el.state
  const scope = el.scope

  const frameWindow = scope.window
  if (!frameWindow || !scope.document)
    return this.warning('Unable to access iframe window or document Scene')

  const originalData = el.call('exec', scope.data) || el.getData()
  const data = deepClone(originalData, undefined, { window: scope.window })

  const isAppMode = scope.sceneMode === 'app'

  let demoApp, demoAppStr, context

  if (isAppMode) {
    // In app mode, the code IS the full app definition — use it as context directly
    const frameProps = el.prepareFrameProps(scope, s)
    demoApp = frameWindow.demoApp = { props: frameProps }
    demoAppStr = el.call('stringifyCode', demoApp, {
      preventExportDefult: true
    })
    context = await el.prepareContext(scope, code)
  } else {
    demoApp = frameWindow.demoApp = el.prepareDemoApp(scope, data, code)
    demoAppStr = el.call('stringifyCode', demoApp, {
      preventExportDefult: true
    })
    context = await el.prepareContext(scope, data)
  }

  const key = scope.rootKey
  context.key = key
  frameWindow.platformState = s.root
  frameWindow.context = context

  await el.prepareDependencies(scope, data)

  frameWindow.contextStr = el.call('stringifyCode', frameWindow.context, {
    preventExportDefult: true
  })

  el.preventLocationChanges(frameWindow)

  // Guard against concurrent renders
  const renderId = Math.random()
  frameWindow.__renderId = renderId

  const appStr = `(async () => {
  try {
    const renderId = ${renderId}

    const previous = document.getElementById('smbls-script')
    if (previous) previous.remove()
    if (window.smblsApp) {
      try { await window.smblsApp.remove() } catch(e) {}
      window.smblsApp = null
    }
    // Clear all rendered content from body
    while (document.body.firstChild) document.body.firstChild.remove()

    // Abort if a newer render has started
    if (window.__renderId !== renderId) return

    const contextStr = ${frameWindow.contextStr}

    const smbls = window.smbls

    if (!smbls || !smbls.create) {
      throw new Error('smbls is not properly loaded or missing create method')
    }

    window.requestAnimationFrame(async() => {
      if (window.__renderId !== renderId) return
      // Bind iframe document/window so smbls renders into the iframe, not the parent
      contextStr.document = document
      contextStr.window = window
      contextStr.container = document.head
      contextStr.parent = document.body
      const smblsApp = await smbls.createSync.call(window, ${demoAppStr}, contextStr)
      if (window.__renderId !== renderId) {
        try { await smblsApp.remove() } catch(e) {}
        return
      }
      window.smblsApp = smblsApp
    })
  } catch (e) {
    console.error('[App] Fatal error:', e)
    window.demoApp.on.error.call(window.smblsApp, e, window.smblsApp)
    throw new Error(e)
  }
})()`

  loadJavascript(
    replaceOctalEscapeSequences(appStr),
    false,
    scope.document,
    'module'
  )
}
